import { cn } from '@/lib/utils';
import { IconInbox, IconTrash, IconArchive, IconMail } from '@tabler/icons-react';
import type { EmailCategory } from '@/lib/cloudflare/types';

// Placeholder card configuration for each column - matches email card structure
const PLACEHOLDER_CONFIG: Record<EmailCategory, {
  icon: typeof IconInbox;
  title: string;
  description: string;
  examples: string[];
  color: string;
  bgColor: string;
}> = {
  do_nothing: {
    icon: IconInbox,
    title: 'Keep in Inbox',
    description: 'Important emails that need your attention',
    examples: ['Personal messages', 'Work emails', 'Action items'],
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/5',
  },
  unsubscribe: {
    icon: IconMail,
    title: 'Unsubscribe',
    description: 'Newsletters and subscriptions you no longer want',
    examples: ['Marketing', 'Newsletters', 'Promos'],
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/5',
  },
  archive: {
    icon: IconArchive,
    title: 'Archive',
    description: 'Emails to keep but move out of inbox',
    examples: ['Receipts', 'Confirmations', 'FYI'],
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/5',
  },
  delete: {
    icon: IconTrash,
    title: 'Delete',
    description: 'Emails you can safely remove',
    examples: ['Expired offers', 'Old notifications', 'Spam'],
    color: 'text-red-500',
    bgColor: 'bg-red-500/5',
  },
};

interface OnboardingPlaceholderCardProps {
  category: EmailCategory;
  className?: string;
}

// Matches email card dimensions: px-2 py-1.5, same text sizes
export function OnboardingPlaceholderCard({
  category,
  className,
}: OnboardingPlaceholderCardProps) {
  const config = PLACEHOLDER_CONFIG[category];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'border-b border-dashed border-border/40',
        config.bgColor,
        className
      )}
    >
      {/* Matches email card: px-2 py-1.5 */}
      <div className="px-2 py-1.5">
        {/* Row 1: Icon + Title (like sender row) */}
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <Icon className={cn('h-3 w-3 shrink-0', config.color)} />
            <span className={cn('text-[11px] font-medium', config.color)}>
              {config.title}
            </span>
          </div>
          <span className="text-[9px] text-muted-foreground/50 shrink-0">
            Example
          </span>
        </div>

        {/* Row 2: Description (like subject row) */}
        <p className="text-[11px] text-foreground/70 truncate">
          {config.description}
        </p>

        {/* Row 3: Examples (like snippet + labels row) */}
        <div className="flex items-center justify-between gap-1">
          <p className="text-[10px] text-muted-foreground truncate flex-1 italic">
            Drag emails here to categorize
          </p>
          <div className="flex items-center gap-1 shrink-0">
            {config.examples.slice(0, 2).map((example) => (
              <span
                key={example}
                className="text-[9px] px-1 py-0.5 rounded bg-muted/50 text-muted-foreground"
              >
                {example}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper to render placeholder for a column
interface ColumnPlaceholderProps {
  category: EmailCategory;
  isVisible: boolean;
  className?: string;
}

export function ColumnPlaceholder({
  category,
  isVisible,
  className,
}: ColumnPlaceholderProps) {
  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'transition-all duration-500 ease-out p-1',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4',
        className
      )}
    >
      <OnboardingPlaceholderCard category={category} />
    </div>
  );
}
